import React from 'react';
import { ArrowLeft, Shield, Mail, FileText, Lock, Users, Database, UserCheck, Building2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logoUrl } from '../mockData';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#1B2380] hover:text-[#0E8C84] transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Torna alla Home</span>
            </button>
            <img src={logoUrl} alt="Assietica" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#1B2380] to-[#0E8C84] rounded-xl flex items-center justify-center">
              <Shield className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1B2380]">
                Informativa Privacy
              </h1>
              <p className="text-gray-600 mt-1">Ai sensi del Regolamento UE 2016/679 (GDPR)</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700">
            {/* Introduzione */}
            <p className="text-gray-700 leading-relaxed mb-8 bg-gray-50 p-4 rounded-lg border-l-4 border-[#1B2380]">
              Ai sensi e per gli effetti di cui agli artt. 13 e 14 del Regolamento UE 2016/679, 
              <strong> Assietica Società Cooperativa Società Benefit</strong> (in seguito anche la "Società"), 
              in qualità di Titolare del trattamento, La informa che i dati personali raccolti, riferibili 
              direttamente all'interessato o acquisiti presso terzi, saranno trattati in modo lecito, 
              corretto e trasparente, secondo le modalità e per le finalità di seguito illustrate.
            </p>

            {/* Sezione 1 - Finalità */}
            <section className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#1B2380]/10 rounded-lg flex items-center justify-center">
                  <FileText className="text-[#1B2380]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#1B2380]">1. Finalità e modalità del trattamento dei dati personali</h2>
              </div>
              
              <p className="mb-4">I dati personali sono trattati nell'ambito della gestione del servizio richiesto secondo le seguenti finalità:</p>
              
              <div className="space-y-4">
                <div className="bg-[#1B2380]/5 rounded-lg p-4 border-l-4 border-[#1B2380]">
                  <h3 className="font-bold text-[#1B2380] mb-2">1. Obblighi contrattuali e precontrattuali</h3>
                  <p className="text-gray-700">Adempimento di obblighi derivanti da un contratto stipulato con l'interessato o esecuzione di specifiche richieste pervenute dall'Interessato prima della conclusione del contratto, nonché attività connesse e strumentali alla gestione del rapporto.</p>
                </div>
                
                <div className="bg-[#0E8C84]/5 rounded-lg p-4 border-l-4 border-[#0E8C84]">
                  <h3 className="font-bold text-[#0E8C84] mb-2">2. Tutela del patrimonio</h3>
                  <p className="text-gray-700">Attività connesse alla gestione e tutela del patrimonio aziendale.</p>
                </div>
                
                <div className="bg-[#0E8C84]/5 rounded-lg p-4 border-l-4 border-[#0E8C84]">
                  <h3 className="font-bold text-[#0E8C84] mb-2">3. Obblighi di legge</h3>
                  <p className="text-gray-700">Adempimento di obblighi previsti dalla normativa nazionale ed europea, nonché da disposizioni impartite da Pubbliche Autorità o Organismi di Vigilanza (es. IVASS).</p>
                </div>
                
                <div className="bg-[#8FE3DA]/10 rounded-lg p-4 border-l-4 border-[#8FE3DA]">
                  <h3 className="font-bold text-[#E67E22] mb-2">4. Finalità Commerciali (previo consenso)</h3>
                  <ul className="space-y-2 mt-3 text-gray-700">
                    <li><strong>4.a)</strong> Promozione e offerta di prodotti/servizi propri della Società.</li>
                    <li><strong>4.b)</strong> Comunicazione e offerta di prodotti/servizi di soggetti terzi.</li>
                    <li><strong>4.c)</strong> Ricerche di mercato volte a rilevare il grado di soddisfazione dell'Interessato.</li>
                    <li><strong>4.d)</strong> Comunicazione di dati a terzi per finalità promozionali di questi ultimi.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                <p className="text-gray-700 text-sm">
                  <strong>Nota:</strong> Per le finalità di cui al punto 4, l'Interessato ha il diritto di non acconsentire o di opporsi in qualsiasi momento. Il mancato consenso per tali finalità non pregiudica l'erogazione dei servizi principali (punti 1, 2 e 3).
                </p>
              </div>

              <div className="bg-[#1B2380]/5 rounded-lg p-4 mt-4">
                <h3 className="font-bold text-[#1B2380] mb-2">Modalità e Conservazione</h3>
                <p className="text-gray-700">
                  Il trattamento avviene mediante strumenti manuali, informatici e telematici con logiche correlate alle finalità e nel rispetto della sicurezza. I dati saranno conservati per un termine di <strong>10 anni</strong> dalla data di cessazione del servizio, per finalità di accertamento, esercizio o difesa di un diritto in sede giudiziaria.
                </p>
              </div>
            </section>

            {/* Sezione 2 - Comunicazione */}
            <section className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#0E8C84]/10 rounded-lg flex items-center justify-center">
                  <Users className="text-[#0E8C84]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#0E8C84]">2. Ambito di comunicazione dei dati</h2>
              </div>
              
              <p className="mb-4">I dati potranno essere comunicati, nel rispetto delle finalità indicate, ai seguenti soggetti:</p>
              
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-[#0E8C84]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-[#0E8C84] rounded-full"></span>
                  </span>
                  <span><strong>Autorità competenti:</strong> In ottemperanza a obblighi di legge o regolamenti (es. Organi di vigilanza e controllo).</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-[#0E8C84]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-[#0E8C84] rounded-full"></span>
                  </span>
                  <span><strong>Società del Gruppo:</strong> Società controllate, collegate (ai sensi dell'art. 2359 c.c.) o enti partner che operano come distinti titolari o responsabili del trattamento.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-[#0E8C84]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-[#0E8C84] rounded-full"></span>
                  </span>
                  <span><strong>Personale incaricato:</strong> Dipendenti, collaboratori, consulenti, stagisti e lavoratori interinali della Società, appositamente nominati e istruiti.</span>
                </li>
              </ul>
            </section>

            {/* Sezione 3 - Dati Particolari */}
            <section className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#E74C3C]/10 rounded-lg flex items-center justify-center">
                  <Lock className="text-[#E74C3C]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#E74C3C]">3. Trattamento di Dati Particolari (Dati Sensibili)</h2>
              </div>
              
              <p className="leading-relaxed">
                La Società, limitatamente a quanto necessario per le finalità assicurative, potrebbe venire a conoscenza di dati particolari (es. dati relativi alla salute, convinzioni religiose, appartenenza sindacale, ecc.). Tali dati saranno trattati esclusivamente con il <strong>consenso esplicito dell'Interessato</strong>, adottando tutte le misure di sicurezza necessarie previste dal Regolamento.
              </p>
            </section>

            {/* Sezione 4 - Natura del conferimento */}
            <section className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#0E8C84]/10 rounded-lg flex items-center justify-center">
                  <Database className="text-[#0E8C84]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#0E8C84]">4. Natura del conferimento dei dati e rifiuto</h2>
              </div>
              
              <p className="mb-4">
                Il conferimento dei dati per le finalità di cui al paragrafo 1 (punti 1, 2 e 3) è <strong>necessario</strong> per espletare la pratica richiesta.
              </p>
              
              <div className="space-y-3">
                <div className="bg-[#E74C3C]/5 rounded-lg p-4 border-l-4 border-[#E74C3C]">
                  <h3 className="font-bold text-[#E74C3C] mb-2">Conseguenze del rifiuto</h3>
                  <p className="text-gray-700">L'eventuale rifiuto di fornire tali dati comporterebbe l'impossibilità per Assietica Società Cooperativa Società Benefit di dare corso alla pratica o erogare il servizio.</p>
                </div>
                
                <div className="bg-[#0E8C84]/5 rounded-lg p-4 border-l-4 border-[#0E8C84]">
                  <h3 className="font-bold text-[#0E8C84] mb-2">Revoca del consenso</h3>
                  <p className="text-gray-700">Il consenso può essere revocato in ogni momento, senza pregiudicare la liceità dei trattamenti effettuati precedentemente.</p>
                </div>
              </div>
            </section>

            {/* Sezione 5 - Diritti */}
            <section className="mb-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#1B2380]/10 rounded-lg flex items-center justify-center">
                  <UserCheck className="text-[#1B2380]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#1B2380]">5. Diritti dell'Interessato</h2>
              </div>
              
              <p className="mb-4">L'Interessato può esercitare i diritti previsti dagli artt. 15-22 del GDPR:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Accesso</h3>
                  <p className="text-gray-700 text-sm">Conferma dell'esistenza di trattamenti e accesso ai dati.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Rettifica e Integrazione</h3>
                  <p className="text-gray-700 text-sm">Correzione di dati inesatti o integrazione di quelli incompleti.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Cancellazione (Oblio)</h3>
                  <p className="text-gray-700 text-sm">Rimozione dei dati nei casi previsti dal Regolamento.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Limitazione</h3>
                  <p className="text-gray-700 text-sm">Restrizione del trattamento in particolari ipotesi.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Portabilità</h3>
                  <p className="text-gray-700 text-sm">Ricezione dei dati in formato strutturato e leggibile.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-[#1B2380] mb-2">Opposizione</h3>
                  <p className="text-gray-700 text-sm">Diritto di opporsi al trattamento per motivi legittimi o per finalità di marketing.</p>
                </div>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                <p className="text-gray-700 text-sm">
                  <strong>Processi automatizzati:</strong> La Società informa di non utilizzare processi decisionali interamente automatizzati o attività di profilazione senza esplicito consenso.
                </p>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 mt-4">
                <p className="text-gray-700 text-sm">
                  <strong>Conservazione dei dati e dei documenti caricati:</strong> I dati personali e gli eventuali documenti trasmessi tramite i moduli del sito (es. visure camerali, documenti d'identità) sono conservati per il tempo strettamente necessario alla gestione della richiesta e comunque per un periodo massimo di 24 mesi dalla ricezione, salvo che alla richiesta faccia seguito la stipula di un contratto, nel qual caso la conservazione seguirà i termini di legge applicabili ai rapporti contrattuali e assicurativi. Decorso tale periodo, i dati e i documenti vengono cancellati o resi anonimi. L'interessato può richiederne in qualsiasi momento la cancellazione anticipata scrivendo a info@assietica.it.
                </p>
              </div>
            </section>

            {/* Sezione 6 - Titolare */}
            <section className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#8FE3DA]/20 rounded-lg flex items-center justify-center">
                  <Building2 className="text-[#E67E22]" size={20} />
                </div>
                <h2 className="text-xl font-bold text-[#E67E22]">6. Titolare del Trattamento e Contatti</h2>
              </div>
              
              <div className="bg-[#1B2380]/5 rounded-xl p-6 border border-[#1B2380]/10">
                <p className="font-semibold text-gray-800 mb-4">Il Titolare del trattamento è:</p>
                <h3 className="text-xl font-bold text-[#1B2380] mb-4">Assietica Società Cooperativa Società Benefit</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Building2 className="text-[#0E8C84] flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Sede Legale</p>
                      <p className="text-gray-800">Viale Antonio Gramsci 8/G - 08015 Macomer (NU)</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="text-[#0E8C84] flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Email di riferimento</p>
                      <a href="mailto:agenzia@assietica.it" className="text-[#1B2380] hover:text-[#0E8C84] font-medium">agenzia@assietica.it</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="text-[#0E8C84] flex-shrink-0 mt-1" size={18} />
                    <div>
                      <p className="text-sm text-gray-500">Email Titolare/DPO</p>
                      <a href="mailto:umberto@assietica.it" className="text-[#1B2380] hover:text-[#0E8C84] font-medium">umberto@assietica.it</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#8FE3DA]/10 rounded-lg p-4 mt-4 border border-[#8FE3DA]/20">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="text-[#E67E22] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-gray-700">
                      L'Interessato ha inoltre il diritto di proporre reclamo al <strong>Garante per la protezione dei dati personali</strong>.
                    </p>
                    <a 
                      href="https://www.garanteprivacy.it" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-[#1B2380] hover:text-[#0E8C84] font-medium underline"
                    >
                      www.garanteprivacy.it →
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Back button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#1B2380] to-[#0E8C84] text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Torna alla Home
            </button>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#10154F] text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © {new Date().getFullYear()} Assietica Società Cooperativa. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
